<template>
  <div class="quick-edit-course">
    <div class="header">
      <h2>Quick Edit Courses</h2>
      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search courses..."
          class="search-input"
        >
        <i class="fas fa-search search-icon"></i>
      </div>
      <div class="loading" v-if="loading">Loading courses...</div>
      <div class="error" v-if="error">{{ error }}</div>
    </div>

    <div class="courses-list" v-if="filteredCourses.length && !editingCourse">
      <div v-for="course in filteredCourses" :key="course.id" class="course-item">
        <div class="course-info">
          <h3>{{ course.title }}</h3>
          <p>{{ course.description }}</p>
        </div>
        <div class="course-actions">
          <button class="edit-btn" @click="editCourse(course)">
            <i class="fas fa-edit"></i> Edit
          </button>
        </div>
      </div>
    </div>

    <div class="no-courses" v-else-if="!loading && !editingCourse">
      {{ searchQuery ? 'No courses found matching your search' : 'No courses found' }}
    </div>

    <!-- Edit Course Form -->
    <div v-if="editingCourse" class="edit-course-form">
      <div class="form-header">
        <h3>Edit Course: {{ editingCourse.title }}</h3>
        <button class="close-btn" @click="cancelEdit">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="saveCourseChanges">
        <div class="form-group">
          <label for="title">Title</label>
          <input 
            type="text" 
            id="title" 
            v-model="editedCourse.title" 
            class="form-input"
            required
          >
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <QuillEditor 
            id="description" 
            v-model:content="editedCourse.description" 
            label="Description"
            :toolbar="toolbarOptions"
            placeholder="Enter course description"
            required
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="minutes">Duration (minutes)</label>
            <input 
              type="number" 
              id="minutes" 
              v-model="editedCourse.minutes" 
              class="form-input"
              required
            >
          </div>

          <div class="form-group">
            <label for="price">Price</label>
            <input 
              type="number" 
              id="price" 
              v-model="editedCourse.price" 
              class="form-input"
              step="0.01"
              required
            >
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="category_id">Category</label>
            <select id="category_id" v-model="editedCourse.category_id" class="form-select" required>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.title }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="instructor_id">Instructor</label>
            <select id="instructor_id" v-model="editedCourse.instructor_id" class="form-select" required>
              <option v-for="instructor in instructors" :key="instructor.id" :value="instructor.user_id">
                {{ instructor.first_name }} {{ instructor.last_name }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group checkbox-group">
            <label>
              <input 
                type="checkbox" 
                v-model="editedCourse.is_premium"
              > Premium Course
            </label>
          </div>

          <div class="form-group checkbox-group">
            <label>
              <input 
                type="checkbox" 
                v-model="editedCourse.top_course"
              > Top Course
            </label>
          </div>

          <div class="form-group checkbox-group">
            <label>
              <input 
                type="checkbox" 
                v-model="editedCourse.is_active"
              > Active
            </label>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="cancelEdit">Cancel</button>
          <button type="submit" class="save-btn" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'QuickEditCourse',
  components: {
    QuillEditor
  },
  data() {
    return {
      courses: [],
      loading: false,
      error: null,
      searchQuery: '',
      editingCourse: null,
      editedCourse: null,
      saving: false,
      categories: [],
      instructors: [],
      toolbarOptions: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['clean'],
        ['link']
      ]
    }
  },
  computed: {
    filteredCourses() {
      if (!this.searchQuery) return this.courses;
      
      const query = this.searchQuery.toLowerCase();
      return this.courses.filter(course => 
        course.title.toLowerCase().includes(query) ||
        course.description.toLowerCase().includes(query)
      );
    }
  },
  async created() {
    await this.fetchCourses();
    await this.fetchCategories();
    await this.fetchInstructors();
  },
  methods: {
    async fetchCourses() {
      this.loading = true;
      this.error = null;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get('https://api.kewaar.com/api/admin/courses', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        this.courses = response.data.data || [];
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to fetch courses. Please try again.';
        console.error('Error fetching courses:', err);
      } finally {
        this.loading = false;
      }
    },
    async fetchCategories() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/categories', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        this.categories = response.data.data || [];
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    },
    async fetchInstructors() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/instructors', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        this.instructors = response.data.data || [];
      } catch (err) {
        console.error('Error fetching instructors:', err);
      }
    },
    async editCourse(course) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`https://api.kewaar.com/api/admin/courses/${course.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        this.editingCourse = response.data.data;
        this.editedCourse = { ...response.data.data };
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to fetch course details. Please try again.';
        console.error('Error fetching course details:', err);
      }
    },
    cancelEdit() {
      this.editingCourse = null;
      this.editedCourse = null;
    },
    async saveCourseChanges() {
      this.saving = true;
      this.error = null;
      
      try {
        const token = localStorage.getItem('token');
        await axios.put(`https://api.kewaar.com/api/admin/courses/${this.editingCourse.id}`, this.editedCourse, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        // Update the course in the list
        const index = this.courses.findIndex(c => c.id === this.editingCourse.id);
        if (index !== -1) {
          this.courses[index] = { ...this.editedCourse };
        }
        
        this.cancelEdit();
        this.$emit('course-updated', this.editedCourse);
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to save course changes. Please try again.';
        console.error('Error saving course changes:', err);
      } finally {
        this.saving = false;
      }
    }
  }
}
</script>

<style scoped>
.quick-edit-course {
  padding: 1.5rem;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header {
  margin-bottom: 1.5rem;
}

.header h2 {
  color: #333;
  margin-bottom: 1rem;
}

.search-container {
  position: relative;
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #2196F3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.loading {
  color: #666;
  font-style: italic;
}

.error {
  color: #dc3545;
  padding: 0.5rem;
  background-color: #f8d7da;
  border-radius: 4px;
  margin-top: 0.5rem;
}

.courses-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.course-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.course-info h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.course-info p {
  margin: 0.5rem 0 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.course-actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.edit-btn:hover {
  background-color: #1976D2;
}

.no-courses {
  text-align: center;
  color: #6c757d;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}

/* Edit Form Styles */
.edit-course-form {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
  border: 1px solid #e9ecef;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.form-header h3 {
  margin: 0;
  color: #2c3e50;
}

.close-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
}

.close-btn:hover {
  color: #dc3545;
}

.form-group {
  margin-bottom: 1rem;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-row .form-group {
  flex: 1;
  margin-bottom: 0;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

.form-input, .form-select, .form-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus, .form-select:focus, .form-textarea:focus {
  outline: none;
  border-color: #2196F3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  cursor: pointer;
}

.checkbox-group input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background-color: #e9ecef;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.save-btn:hover {
  background-color: #1976D2;
}

.save-btn:disabled {
  background-color: #90caf9;
  cursor: not-allowed;
}
</style> 